// Guide pages (v1.82.0): one "How we do things here" page beside each board —
// "How we film", the hashtag list, the posting checklist — in an editor that
// finally does document bones. Fold-away sections are a READER behavior:
// authors just write headings (the editor's normal formatting), and any
// heading becomes a tap-to-open section, so a long guide reads in ten
// seconds instead of forty lines. Content stays plain sanitized HTML.
import { useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, Pencil, X } from 'lucide-react';
import type { ProjectGuide } from '@cg-projects/shared';
import { api, ApiClientError } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { RichEditor, type RichEditorHandle } from '@/core/ui/RichEditor';
import { Spinner } from '@/core/ui/atoms';
import { useToastStore } from '@/core/ui/toastStore';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';

interface Section { heading: string | null; html: string }

/** Split sanitized guide HTML into sections at h1/h2/h3 boundaries. Content
 *  before the first heading becomes an always-visible preamble. */
function splitSections(html: string): Section[] {
  const root = document.createElement('div');
  root.innerHTML = html;
  const sections: Section[] = [];
  let current: Section = { heading: null, html: '' };
  for (const node of Array.from(root.childNodes)) {
    const tag = node instanceof HTMLElement ? node.tagName : '';
    if (tag === 'H1' || tag === 'H2' || tag === 'H3') {
      if (current.heading !== null || current.html.trim()) sections.push(current);
      current = { heading: (node as HTMLElement).textContent ?? '', html: '' };
    } else {
      current.html += node instanceof HTMLElement ? node.outerHTML : (node.textContent ?? '');
    }
  }
  if (current.heading !== null || current.html.trim()) sections.push(current);
  return sections;
}

export function GuidePage({ areaId, areaName, onClose }: { areaId: number; areaName: string; onClose: () => void }) {
  const toast = useToastStore((s) => s.show);
  const [guide, setGuide] = useState<ProjectGuide | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const editorRef = useRef<RichEditorHandle | null>(null);
  useEscapeToClose(() => { if (!editing) onClose(); });

  useEffect(() => {
    let dead = false;
    api.get<{ guide: ProjectGuide }>(`/areas/${areaId}/guide`)
      .then((r) => { if (!dead) setGuide(r.guide); })
      .catch((e) => { if (!dead) setError(e instanceof Error ? e.message : 'Could not load the guide.'); });
    return () => { dead = true; };
  }, [areaId]);

  const sections = useMemo(() => (guide && !editing ? splitSections(guide.bodyHtml) : []), [guide, editing]);
  const foldable = sections.filter((s) => s.heading !== null).length > 1;

  const save = async () => {
    if (!guide || busy) return;
    setBusy(true);
    try {
      const html = editorRef.current?.getHtml() ?? '';
      const r = await api.put<{ guide: ProjectGuide }>(`/areas/${areaId}/guide`, {
        bodyHtml: html, expectedUpdatedAt: guide.updatedAt,
      });
      setGuide(r.guide);
      setEditing(false);
      toast('Guide saved — everyone on this project sees it now.', 'info');
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 409) {
        toast(e.message, 'error');
      } else {
        toast(e instanceof Error ? e.message : 'Could not save the guide.', 'error');
      }
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[55] flex flex-col bg-page animate-fade-in">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h1 className="flex min-w-0 items-center gap-2 text-[15px] font-semibold text-ink">
          <Icon i={BookOpen} size={16} /> <span className="truncate">How we do things — {areaName}</span>
        </h1>
        <div className="flex shrink-0 items-center gap-2">
          {!editing && guide && (
            <button onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1 text-xs text-muted hover:bg-hover hover:text-ink">
              <Icon i={Pencil} size={14} /> Edit
            </button>
          )}
          {editing && (
            <>
              <button onClick={() => setEditing(false)} disabled={busy}
                className="rounded-md border border-line px-2.5 py-1 text-xs text-muted hover:bg-hover">Cancel</button>
              <button onClick={() => void save()} disabled={busy}
                className="rounded-md bg-brand px-3 py-1 text-xs font-semibold text-page hover:brightness-110 disabled:opacity-50">
                {busy ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
          <button
            onClick={() => {
              if (editing && !window.confirm('Close without saving? Your edits will be lost.')) return;
              onClose();
            }}
            className="rounded p-1.5 text-faint hover:text-ink" title="Close"
          ><Icon i={X} size={16} /></button>
        </div>
      </div>

      <div className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto p-4 md:p-6">
        {error && <p className="text-[13px] text-danger">{error}</p>}
        {!error && !guide && <div className="flex justify-center py-10"><Spinner /></div>}

        {guide && editing && (
          <RichEditor ref={editorRef} initialHtml={guide.bodyHtml} autoFocus minHeight="60vh"
            placeholder="How this team works — filming steps, the hashtag list, the posting checklist…"
            onPasteFiles={() => {
              // A pasted screenshot would either blow the size cap as base64 or
              // die on reload as a blob: URL — refuse honestly instead.
              toast('Pictures can’t be pasted into a guide yet — use words and links.', 'error');
              return true;
            }} />
        )}

        {guide && !editing && (
          guide.bodyHtml.trim() ? (
            <div className="space-y-1">
              {sections.map((s, i) => s.heading === null ? (
                <div key={i} className="cg-prose cg-prose-reading mb-3" dangerouslySetInnerHTML={{ __html: s.html }} />
              ) : (
                <div key={i} className="rounded-lg border border-line">
                  <button
                    onClick={() => setOpen((o) => { const n = new Set(o); if (n.has(i)) n.delete(i); else n.add(i); return n; })}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[14px] font-semibold text-ink hover:bg-hover"
                  >
                    <Icon i={foldable && !open.has(i) ? ChevronRight : ChevronDown} size={14} />
                    {s.heading}
                  </button>
                  {(!foldable || open.has(i)) && (
                    <div className="cg-prose cg-prose-reading border-t border-line px-3 py-3" dangerouslySetInnerHTML={{ __html: s.html }} />
                  )}
                </div>
              ))}
              {guide.updatedByName && (
                <p className="pt-3 text-[11px] text-faint">Last touched by {guide.updatedByName}.</p>
              )}
            </div>
          ) : (
            <div className="py-10 text-center">
              <p className="mb-2 text-[14px] text-muted">Nothing written yet.</p>
              <p className="mx-auto mb-5 max-w-sm text-[12.5px] leading-relaxed text-faint">
                This page is for how {areaName} actually works — filming steps, the hashtag list, the posting checklist — living right beside the board instead of in a group chat.
              </p>
              <button onClick={() => setEditing(true)}
                className="rounded-md bg-brand px-4 py-2 text-[13px] font-semibold text-page hover:brightness-110">
                Write the first version
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
}
